import { Workbook } from "exceljs";
import { EventRatingsResponse } from "../../services/reports.service";
import { applyHeaderStyle } from "./workbook-styles";

export const addEventRatingsSheets = (
	workbook: Workbook,
	data: EventRatingsResponse,
): void => {
	if (!data.chart) return;
	if (data.chart.ratingType === "SCORED_SUM" && data.chart.scoredSum) {
		addScoredSumSheets(workbook, data);
	} else if (
		data.chart.ratingType === "INDEPENDENT_RATINGS" &&
		data.chart.independent
	) {
		addIndependentSheets(workbook, data);
	}
};

const PROGRAM_DAYS = 14;

/**
 * SIMS-5825 — the per-enrollment sheet, one column block per program day.
 *
 * Two shapes, matching the two rating types the report supports:
 *   SCORED_SUM          D1..D14 plus Avg / Min / Max
 *   INDEPENDENT_RATINGS D1 emoji, D1 distress, ... D14 emoji, D14 distress
 *
 * A day with no submission is left blank rather than zero — nothing measured is
 * not the same as measuring zero, the same rule the emoji sheet follows.
 *
 * Emoji cells carry the raw value rather than a label, for the same reason the
 * emoji sheet's headers do: the API exposes bare values with no labels, and this
 * app's EmojiBadge and the design mock read the ordering in opposite directions.
 */
const addPerEnrollmentSheet = (
	workbook: Workbook,
	data: EventRatingsResponse,
	scored: boolean,
): void => {
	const sheet = workbook.addWorksheet("Ratings per enrollment");

	const dayColumns = scored
		? Array.from({ length: PROGRAM_DAYS }, (_, i) => ({
				header: `D${i + 1}`,
				key: `d${i + 1}`,
				width: 8,
			}))
		: Array.from({ length: PROGRAM_DAYS }, (_, i) => [
				{ header: `D${i + 1} emoji`, key: `d${i + 1}emoji`, width: 12 },
				{ header: `D${i + 1} distress`, key: `d${i + 1}distress`, width: 12 },
			]).flat();

	sheet.columns = [
		{ header: "Enrollment ID", key: "enrollmentId", width: 28 },
		{ header: "Participant", key: "participantName", width: 28 },
		...dayColumns,
		...(scored
			? [
					{ header: "Avg", key: "avgScore", width: 10 },
					{ header: "Min", key: "minScore", width: 10 },
					{ header: "Max", key: "maxScore", width: 10 },
				]
			: []),
	];
	applyHeaderStyle(sheet.getRow(1));

	for (const e of data.enrollments) {
		const row: Record<string, unknown> = {
			enrollmentId: e.enrollmentId,
			participantName: e.participantName,
		};
		for (const d of e.byDay ?? []) {
			if (scored) {
				row[`d${d.day}`] = d.score ?? "";
			} else {
				row[`d${d.day}emoji`] = d.emoji ?? "";
				row[`d${d.day}distress`] = d.distress ?? "";
			}
		}
		if (scored) {
			row.avgScore = e.avgScore ?? "";
			row.minScore = e.minScore ?? "";
			row.maxScore = e.maxScore ?? "";
		}
		const added = sheet.addRow(row);
		if (scored && typeof e.avgScore === "number") {
			added.getCell("avgScore").numFmt = "0.0";
		}
	}
};

const addScoredSumSheets = (
	workbook: Workbook,
	data: EventRatingsResponse,
): void => {
	const chart = data.chart;
	if (!chart?.scoredSum) return;

	const popAvg = workbook.addWorksheet("Population avg");
	popAvg.columns = [
		{ header: "Day", key: "day", width: 8 },
		{ header: "Avg score", key: "avgScore", width: 12 },
		{ header: "Submissions", key: "submissionCount", width: 14 },
	];
	applyHeaderStyle(popAvg.getRow(1));

	for (const d of chart.scoredSum.avgScoreByDay) {
		const r = popAvg.addRow(d);
		r.getCell("avgScore").numFmt = "0.0";
	}

	addPerEnrollmentSheet(workbook, data, true);
};

const addIndependentSheets = (
	workbook: Workbook,
	data: EventRatingsResponse,
): void => {
	const chart = data.chart;
	if (!chart?.independent) return;
	const ind = chart.independent;

	const distress = workbook.addWorksheet("Distress rating");
	distress.columns = [
		{ header: "Day", key: "day", width: 8 },
		{ header: "Avg distress", key: "avgDistress", width: 14 },
		{ header: "Submissions", key: "submissionCount", width: 14 },
	];
	applyHeaderStyle(distress.getRow(1));
	for (const d of ind.avgDistressByDay) {
		const r = distress.addRow(d);
		r.getCell("avgDistress").numFmt = "0.0";
	}

	const emoji = workbook.addWorksheet("Emoji distribution");
	// SIMS-5825: counts AND the share each represents. The counts alone cannot be
	// compared across days, because the number of submissions per day varies.
	//
	// The header keeps the raw emoji value rather than a friendly name. The API
	// sends `emojiField.values` as bare values ("1", "2", "3") with no labels,
	// and the two are not safely interchangeable: this app's own EmojiBadge reads
	// the FIRST value as the best outcome (🙂, green), while the design mock reads
	// its first column as the worst ("Low", "Poor"). Inventing the mapping here
	// would print a label contradicting the colour the UI shows for the same
	// value. The `(count)` / `%` suffixes give the spec's structure; the labels
	// need either the event schema to expose them or a product decision.
	emoji.columns = [
		{ header: "Day", key: "day", width: 8 },
		...ind.emojiField.values.map((v) => ({
			header: `${v} (count)`,
			key: `emoji_${v}`,
			width: 14,
		})),
		...ind.emojiField.values.map((v) => ({
			header: `${v} %`,
			key: `emojiPct_${v}`,
			width: 12,
		})),
	];
	applyHeaderStyle(emoji.getRow(1));
	for (const d of ind.emojiCountsByDay) {
		const rowData: Record<string, unknown> = { day: d.day };
		// The day's own total, not the population's — a percentage of anything
		// else would not sum to 100 across the row.
		const dayTotal = ind.emojiField.values.reduce(
			(sum, v) => sum + (d.counts[v] ?? 0),
			0,
		);
		for (const v of ind.emojiField.values) {
			const count = d.counts[v] ?? 0;
			rowData[`emoji_${v}`] = count;
			// A day with no submissions gets an empty cell, not 0% — nothing was
			// measured, which is different from measuring zero.
			rowData[`emojiPct_${v}`] =
				dayTotal > 0 ? Math.round((count / dayTotal) * 1000) / 10 : "";
		}
		const r = emoji.addRow(rowData);
		for (const v of ind.emojiField.values) {
			if (dayTotal > 0) r.getCell(`emojiPct_${v}`).numFmt = "0.0";
		}
	}

	addPerEnrollmentSheet(workbook, data, false);
};
